import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ClientDashboardAccess, ClientDashboardAccessPage } from '../../types/client.types';
import { CLIENT_PERMISSIONS_OPTIONS } from '../../types/client-permissions.types';
import {
  PortalDsAlert,
  PortalDsButton,
  PortalDsNoticeBar,
} from 'src/components/portal-design-system';
import { useCreateClientDashboardAccessMutation } from '../../endpoints/client.endpoints';
import notificationService from 'src/services/notification/notification.service';
import { useDispatch } from 'react-redux';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import { SelectionCheckbox } from './selection-checkbox';

interface ManageClientPermissionsProps {
  permissions?: ClientDashboardAccess[];
  onChange?: (permissions: ClientDashboardAccess[]) => void;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  headerActionsEl?: HTMLElement | null;
}

interface PermissionNode {
  page: string;
  tab: string;
  permission: string | null;
  id?: string;
}

const ManageClientPermissions: React.FC<ManageClientPermissionsProps> = ({
  permissions = [],
  onChange,
  disabled = false,
  headerActionsEl,
}) => {
  const { activeClient } = useActiveClient();
  const dispatch = useDispatch();
  const [hasChanges, setHasChanges] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [expandedPageIds, setExpandedPageIds] = useState<string[]>(
    () => CLIENT_PERMISSIONS_OPTIONS.map((page) => page.value)
  );
  const [createClientDashboardAccess] = useCreateClientDashboardAccessMutation();

  // Initialize state from permissions prop using lazy initializer
  const [localPermissions, setLocalPermissions] = useState<PermissionNode[]>(() =>
    permissions.map(p => ({
      page: p.page,
      tab: p.tab,
      permission: p.permission,
      id: p.id,
    }))
  );

  // Use ref to track previous permissions to avoid unnecessary updates
  const prevPermissionsRef = React.useRef<string>('');

  // Sync with external permissions prop
  React.useEffect(() => {
    const newPermissions = permissions.map(p => ({
      page: p.page,
      tab: p.tab,
      permission: p.permission,
      client: activeClient?.ext_client_id,
    }));

    // Create a stable comparison key for permissions
    const permissionsKey = JSON.stringify(newPermissions);

    // Only update if permissions actually changed
    if (prevPermissionsRef.current !== permissionsKey) {
      prevPermissionsRef.current = permissionsKey;
      setLocalPermissions(newPermissions);
    }
  }, [permissions]);

  // Check if a permission is selected
  const isSelected = (page: string, tab: string, permission: string | null): boolean => {
    return localPermissions.some(
      p => p.page === page && p.tab === tab && p.permission === permission
    );
  };

  // Check if a tab is selected (has at least one permission selected OR has a null permission entry)
  const isTabSelected = (page: string, tab: string): boolean => {
    return localPermissions.some(p => p.page === page && p.tab === tab);
  };

  // Check if a page is selected (has at least one tab selected)
  const isPageSelected = (page: string): boolean => {
    return localPermissions.some(p => p.page === page);
  };

  // Get selection state for display
  const getSelectionState = (page: string, tab: string, permission: string | null): 'checked' | 'unchecked' | 'indeterminate' => {
    if (permission !== null) {
      return isSelected(page, tab, permission) ? 'checked' : 'unchecked';
    }

    // For tabs, check if all permissions are selected or some are selected
    const pageData = CLIENT_PERMISSIONS_OPTIONS.find(p => p.value === page);
    const tabData = pageData?.tabs.find(t => t.value === tab);
    const tabPermissions = tabData?.permissions || [];

    // Check if tab is explicitly selected (has null permission entry)
    // This takes priority - if tab has null entry, it's always checked
    const isTabExplicitlySelected = localPermissions.some(
      p => p.page === page && p.tab === tab && p.permission === null
    );

    if (tabPermissions.length === 0) {
      // Tab with no permissions - just check if tab is selected
      return isTabSelected(page, tab) ? 'checked' : 'unchecked';
    }

    // If tab is explicitly selected (null permission entry), it's always checked
    // This ensures the tab stays selected even when permissions are deselected
    if (isTabExplicitlySelected) {
      const selectedPermissions = tabPermissions.filter(perm =>
        isSelected(page, tab, perm.value)
      );
      // If all permissions are selected, show as checked
      if (selectedPermissions.length === tabPermissions.length) {
        return 'checked';
      }
      // If some permissions are selected, show as indeterminate
      if (selectedPermissions.length > 0) {
        return 'indeterminate';
      }
      // If no permissions are selected but tab has null entry, still show as checked
      return 'checked';
    }

    // Tab is not explicitly selected, check permissions
    const selectedPermissions = tabPermissions.filter(perm =>
      isSelected(page, tab, perm.value)
    );

    if (selectedPermissions.length === 0) {
      return 'unchecked';
    }
    if (selectedPermissions.length === tabPermissions.length) {
      return 'checked';
    }
    return 'indeterminate';
  };

  // Helper to update permissions and call onChange
  const updatePermissions = (newPermissions: PermissionNode[]) => {
    setLocalPermissions(newPermissions);
    onChange?.(newPermissions.map(p => ({
      id: p.id,
      client: '',
      page: p.page,
      tab: p.tab,
      permission: p.permission,
    })));
  };

  // Helper to find and select items by their value (for requires)
  // Only selects the item itself, not all permissions of a tab
  const selectByValue = (value: string, newPermissions: PermissionNode[]): void => {
    // Search through all pages, tabs, and permissions to find matching values
    for (const page of CLIENT_PERMISSIONS_OPTIONS) {
      // Check if page value matches
      if (page.value === value) {
        // Select entire page (all tabs with null permission entries)
        for (const tab of page.tabs) {
          if (!newPermissions.some(p => p.page === page.value && p.tab === tab.value && p.permission === null)) {
            newPermissions.push({
              page: page.value,
              tab: tab.value,
              permission: null,
            });
          }
        }
        return;
      }

      // Check tabs
      for (const tab of page.tabs) {
        if (tab.value === value) {
          // Select just the tab itself (null permission entry), NOT all permissions
          if (!newPermissions.some(p => p.page === page.value && p.tab === tab.value && p.permission === null)) {
            newPermissions.push({
              page: page.value,
              tab: tab.value,
              permission: null,
            });
          }
          return;
        }

        // Check permissions
        for (const perm of tab.permissions) {
          if (perm.value === value) {
            // Select just this permission
            if (!newPermissions.some(p => p.page === page.value && p.tab === tab.value && p.permission === perm.value)) {
              newPermissions.push({
                page: page.value,
                tab: tab.value,
                permission: perm.value,
              });
            }
            return;
          }
        }
      }
    }
  };

  // Handle permission toggle
  const handlePermissionToggle = (page: string, tab: string, permission: string | null) => {
    setHasChanges(true);
    if (disabled) return;

    const isCurrentlySelected = isSelected(page, tab, permission);

    if (isCurrentlySelected) {
      // Deselecting: Remove just this permission (ignore required)
      // IMPORTANT: Keep the null permission entry for the tab so it stays selected
      const newPermissions = localPermissions.filter(
        p => !(p.page === page && p.tab === tab && p.permission === permission)
      );

      // Ensure the tab's null permission entry remains (if it exists)
      // This keeps the tab selected even when all permissions are deselected
      const hasNullEntry = localPermissions.some(
        p => p.page === page && p.tab === tab && p.permission === null
      );
      if (hasNullEntry && !newPermissions.some(p => p.page === page && p.tab === tab && p.permission === null)) {
        const existingFromOriginal = localPermissions.find(
          p => p.page === page && p.tab === tab && p.permission === null
        );
        newPermissions.push({
          page,
          tab,
          permission: null,
          id: existingFromOriginal?.id,
        });
      }

      updatePermissions(newPermissions);
    } else {
      // Selecting: Add this permission, parent tab, parent page, and any required items
      const newPermissions: PermissionNode[] = [...localPermissions];

      // Add this permission
      if (!newPermissions.some(p => p.page === page && p.tab === tab && p.permission === permission)) {
        const existingFromOriginal = localPermissions.find(
          p => p.page === page && p.tab === tab && p.permission === permission
        );
        newPermissions.push({
          page,
          tab,
          permission,
          id: existingFromOriginal?.id,
        });
      }

      // Ensure parent tab is explicitly selected (add null permission entry to mark it as selected)
      // This allows the tab to remain selected even if all permissions are later deselected
      const pageData = CLIENT_PERMISSIONS_OPTIONS.find(p => p.value === page);
      const tabData = pageData?.tabs.find(t => t.value === tab);

      if (tabData) {
        // Add null permission entry to mark the tab as explicitly selected
        // This works for both tabs with and without permissions
        if (!newPermissions.some(p => p.page === page && p.tab === tab && p.permission === null)) {
          const existingFromOriginal = localPermissions.find(
            p => p.page === page && p.tab === tab && p.permission === null
          );
          newPermissions.push({
            page,
            tab,
            permission: null,
            id: existingFromOriginal?.id,
          });
        }
      }

      // The page is automatically selected when it has tabs with permissions
      // (handled by isPageSelected)

      // Handle requires:
      // 1. Check if the permission itself has requires
      if (permission !== null && tabData) {
        const permissionData = tabData.permissions.find(p => p.value === permission);
        if (permissionData?.requires) {
          for (const requiredValue of permissionData.requires) {
            selectByValue(requiredValue, newPermissions);
          }
        }
      }

      // 2. Check if the parent tab has requires (select only the required items, not all permissions)
      if (tabData?.requires) {
        for (const requiredValue of tabData.requires) {
          selectByValue(requiredValue, newPermissions);
        }
      }

      updatePermissions(newPermissions);
    }
  };

  // Handle tab toggle
  const handleTabToggle = (page: string, tab: string) => {
    setHasChanges(true);
    if (disabled) return;

    const pageData = CLIENT_PERMISSIONS_OPTIONS.find(p => p.value === page);
    const tabData = pageData?.tabs.find(t => t.value === tab);
    if (!tabData) return;

    const isCurrentlySelected = isTabSelected(page, tab);

    if (isCurrentlySelected) {
      // Deselecting: Remove all permissions for this tab (ignore required)
      const newPermissions = localPermissions.filter(
        p => !(p.page === page && p.tab === tab)
      );
      updatePermissions(newPermissions);
    } else {
      // Selecting: Add all permissions for this tab, and any required items
      const newPermissions: PermissionNode[] = [];

      // Add existing permissions that aren't for this tab
      newPermissions.push(...localPermissions.filter(
        p => !(p.page === page && p.tab === tab)
      ));

      // Preserve IDs for existing permissions in this tab
      const existingTabPermissions = localPermissions.filter(
        p => p.page === page && p.tab === tab
      );

      // Add all permissions for this tab
      if (tabData.permissions.length === 0) {
        // Tab with no permissions - add null permission entry
        const existingTab = existingTabPermissions.find(p => p.permission === null);
        newPermissions.push({
          page,
          tab,
          permission: null,
          id: existingTab?.id,
        });
      } else {
        // Tab with permissions - add all permissions
        tabData.permissions.forEach(perm => {
          const existing = existingTabPermissions.find(
            p => p.permission === perm.value
          );
          newPermissions.push({
            page,
            tab,
            permission: perm.value,
            id: existing?.id,
          });
        });
      }

      // Handle requires: select required items
      if (tabData.requires) {
        for (const requiredValue of tabData.requires) {
          selectByValue(requiredValue, newPermissions);
        }
      }

      updatePermissions(newPermissions);
    }
  };

  // Handle page toggle
  const handlePageToggle = (page: string) => {
    setHasChanges(true);
    if (disabled) return;

    const pageData = CLIENT_PERMISSIONS_OPTIONS.find(p => p.value === page);
    if (!pageData) return;

    const isCurrentlySelected = isPageSelected(page);

    if (isCurrentlySelected) {
      // Deselecting: Remove all permissions and tabs for this page (ignore required)
      const newPermissions = localPermissions.filter(p => p.page !== page);
      updatePermissions(newPermissions);
    } else {
      // Selecting: Add all tabs and permissions for this page, and any required items
      const newPermissions: PermissionNode[] = [];

      // Add existing permissions that aren't for this page
      newPermissions.push(...localPermissions.filter(p => p.page !== page));

      // Preserve IDs for existing permissions in this page
      const existingPagePermissions = localPermissions.filter(p => p.page === page);

      // Add all tabs for this page
      pageData.tabs.forEach(tab => {
        if (tab.permissions.length === 0) {
          // Tab with no permissions - add null permission entry
          const existingTab = existingPagePermissions.find(
            p => p.tab === tab.value && p.permission === null
          );
          newPermissions.push({
            page,
            tab: tab.value,
            permission: null,
            id: existingTab?.id,
          });
        } else {
          // Tab with permissions - add all permissions
          tab.permissions.forEach(perm => {
            const existing = existingPagePermissions.find(
              p => p.tab === tab.value && p.permission === perm.value
            );
            newPermissions.push({
              page,
              tab: tab.value,
              permission: perm.value,
              id: existing?.id,
            });
          });
        }
      });

      // Handle requires: select required items
      if (pageData.requires) {
        for (const requiredValue of pageData.requires) {
          selectByValue(requiredValue, newPermissions);
        }
      }

      updatePermissions(newPermissions);
    }
  };

  const handleSave = () => {
    try {
      if (!activeClient?.ext_client_id) return;
      createClientDashboardAccess(localPermissions.map(p => ({
        client: activeClient.ext_client_id,
        page: p.page,
        tab: p.tab,
        permission: p.permission,
      })));
      setHasChanges(false);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 1600);
      notificationService.handleSuccess(null, dispatch, 'Client permissions saved successfully');
    } catch (error) {
      notificationService.handleError(error, dispatch, 'Error saving client permissions');
    }
  };

  const togglePageExpanded = (page: string) => {
    setExpandedPageIds((current) => (
      current.includes(page)
        ? current.filter((id) => id !== page)
        : [...current, page]
    ));
  };

  const renderTreeNode = (page: ClientDashboardAccessPage) => {
    const expanded = expandedPageIds.includes(page.value);
    const pageState = isPageSelected(page.value) ? 'checked' : 'unchecked';

    return (
      <div
        key={page.value}
        className="border-b border-[#EDEEF2] last:border-b-0"
      >
        <div className="flex items-center gap-2.5 bg-[#F7F8FA] px-4 py-3">
          <SelectionCheckbox
            state={pageState}
            disabled={disabled}
            onClick={() => handlePageToggle(page.value)}
          />
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            onClick={() => togglePageExpanded(page.value)}
          >
            {expanded ? (
              <ChevronDown className="size-[15px] shrink-0 text-[#9CA3AF]" />
            ) : (
              <ChevronRight className="size-[15px] shrink-0 text-[#9CA3AF]" />
            )}
            <span className="text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
              {page.name}
            </span>
          </button>
        </div>

        {expanded ? page.tabs.map((tab) => {
          const tabState = getSelectionState(page.value, tab.value, null);
          return (
            <div key={`${page.value}-${tab.value}`}>
              <div className="flex items-center gap-2.5 py-2.5 pl-10 pr-4">
                <SelectionCheckbox
                  state={tabState}
                  disabled={disabled}
                  onClick={() => handleTabToggle(page.value, tab.value)}
                />
                <span className="flex-1 text-[13.5px] font-semibold text-[#0D0F1A]">
                  {tab.name}
                </span>
              </div>
              {tab.permissions.map((permission) => {
                const permissionState = getSelectionState(page.value, tab.value, permission.value);
                return (
                  <div
                    key={`${page.value}-${tab.value}-${permission.value}`}
                    className="flex items-center gap-2.5 py-2 pl-16 pr-4"
                  >
                    <SelectionCheckbox
                      state={permissionState}
                      disabled={disabled}
                      onClick={() => handlePermissionToggle(page.value, tab.value, permission.value)}
                    />
                    <span className="text-[13px] text-[#0D0F1A]">
                      {permission.name}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        }) : null}
      </div>
    );
  };

  const saveButton = (
    <PortalDsButton
      variant="primary"
      size="sm"
      disabled={!hasChanges}
      onClick={handleSave}
    >
      Save changes
    </PortalDsButton>
  );

  return (
    <div className="flex flex-col gap-4">
      {headerActionsEl ? createPortal(saveButton, headerActionsEl) : (
        <div className="flex justify-end">
          {saveButton}
        </div>
      )}

      {savedFlash ? (
        <PortalDsAlert variant="success">
          Permission changes saved.
        </PortalDsAlert>
      ) : null}

      <PortalDsNoticeBar>
        Reporting and Performance are the primary product surfaces going forward. Surveys reflects
        the legacy toolset and is kept for existing clients.
      </PortalDsNoticeBar>

      <div className="overflow-hidden rounded-xl border border-[#EDEEF2]">
        {CLIENT_PERMISSIONS_OPTIONS.length === 0 ? (
          <div className="p-4 text-center text-[13.5px] text-[#5E6278]">
            No permissions available
          </div>
        ) : (
          CLIENT_PERMISSIONS_OPTIONS.map((page) => renderTreeNode(page))
        )}
      </div>
    </div>
  );
};

export default ManageClientPermissions;
